// The chart's Weinstein stage band (DWLF-382): one faint shaded span per stage, from when the stage became knowable to
// when the next one did (or now). Pure, so it is testable without rendering the chart. The stages come from
// GET /v2/stages/{symbol} (the platform's one read rule); this file only draws them. The band is opt-in (off by default)
// and carries no verification caveat itself: the Desk panel shows the stage with its "under forward test" label.

const STAGE = {
  1: { code: 'S1', name: 'base', color: '#94a3b8' },
  2: { code: 'S2', name: 'advance', color: '#22c55e' },
  3: { code: 'S3', name: 'top', color: '#f59e0b' },
  4: { code: 'S4', name: 'decline', color: '#ef4444' },
};

const ISO = (ms) => (Number.isFinite(ms) ? new Date(ms).toISOString().slice(0, 10) : '–');

/**
 * Stage spans, oldest first: [{ stage, from, to }]. `history` is the API's newest-first list of
 * { to, knowableFrom }; the newest span runs to `now`.
 */
export const stageSpans = ({ history, now }) => {
  const rows = (Array.isArray(history) ? history : [])
    .map((h) => ({ stage: h?.to, from: Date.parse(h?.knowableFrom) }))
    .filter((h) => STAGE[h.stage] && Number.isFinite(h.from))
    .sort((a, b) => a.from - b.from);
  return rows
    .map((r, i) => ({ ...r, to: i + 1 < rows.length ? rows[i + 1].from : now }))
    .filter((r) => Number.isFinite(r.to) && r.to > r.from);
};

/**
 * Series entries for the band, or [] when there is nothing to draw (no stage, no bars, or no price extent). Each span
 * is an `area` series: a plain filled path with no click handler, so clicks on the plot still reach the chart (a
 * `position` overlay would swallow them). It has one point per bar inside the span, because the chart keeps only an
 * area's points inside the visible window; a span drawn from its two ends alone vanishes once either end scrolls off.
 * `showInTooltip: false` keeps the crosshair snapping to the candles, not to the band's top edge.
 */
export const buildStageBand = ({ stage, now, minLow, maxHigh, barTimes }) => {
  if (!stage || stage.stage == null || !Number.isFinite(minLow) || !Number.isFinite(maxHigh) || minLow >= maxHigh) return [];
  const bars = (Array.isArray(barTimes) ? barTimes : []).filter(Number.isFinite).sort((a, b) => a - b);
  if (!bars.length) return [];
  return stageSpans({ history: stage.history, now })
    .map((span) => {
      const s = STAGE[span.stage];
      // Half-open [from, to): a boundary bar belongs to the stage that starts there. The area closes on the next
      // span's first bar (or `to` itself for the newest span), so a stage with one bar so far still draws.
      const inside = bars.filter((t) => t >= span.from && t < span.to);
      const close = bars.find((t) => t >= span.to) ?? span.to;
      return {
        key: `Weinstein ${s.code} ${ISO(span.from)}`,
        type: 'area',
        data: inside.length ? [...inside, close].map((t) => ({ t, v: maxHigh })) : [],
        // Full height (DWLF-459): the pane top to bottom whatever the axis resolves to; `v` is only a placeholder.
        style: { color: s.color, opacity: 0.07, fullHeight: true },
        showInTooltip: false,
      };
    })
    .filter((series) => series.data.length >= 2);
};
